import { computed, ref, watch, type Ref } from 'vue';
import { elementLabelsById, ruleCycleSaveMessage } from '@record-fill';
import { useFormBuilderToast } from './useFormBuilderToast';
import {
  ACTIVE_RULE_CREATION_DRAFT_USAGE_ID,
  appendRuleOptionUsage,
  getAvailableRuleTriggerOptions,
  getPendingRuleCreationDraftUsageId,
  getRuleTriggerOptions,
  type RuleOptionUsage,
} from '../lib/rule-trigger-options';
import { flattenRuleFields, getRuleTargetOptions } from '../lib/rule-target-options';
import { useFormBuilderStore } from '../model/form-builder.store';
import {
  BLANK_RULE_NAME_ERROR,
  isRuleCycleError,
  useFormRulesStore,
  type ConditionalFormRule,
  type ConditionalRuleAction,
  type ConditionalRuleTarget,
  type ConditionalThenBlock,
} from '../model/form-rules.store';
import type {
  RuleThenBlockView,
  RuleView,
  SavedThenActionPayload,
  SavedThenIndexedPayload,
} from './form-builder-rules-sidebar.types';

interface ConfirmationMessages {
  changeTriggerOption: {
    title: string;
    description: string;
  };
  clearThenItems: {
    title: string;
    description: string;
  };
  deleteRule: {
    title: string;
    description: string;
  };
}

interface UseSavedFormRulesOptions {
  activeDraftThenBlockId: Ref<string | null>;
  confirmationMessages: ConfirmationMessages;
  getSelectionSignature: () => string;
  lastSelectionSignature: Ref<string>;
  openConfirmationModal: (title: string, description: string, onConfirm: () => void) => void;
}

function isBlankRuleNameError(error: unknown) {
  return error instanceof Error && error.message === BLANK_RULE_NAME_ERROR;
}

function buildRulePatch(
  rule: RuleView,
  thenBlocksPatch: readonly RuleThenBlockView[] = rule.thenBlocks
) {
  const normalizedThenBlocks = thenBlocksPatch.map((block) => ({
    id: block.id,
    action: block.action,
    targets: block.targets.map((target) => ({
      id: target.id,
      type: target.type,
      label: target.label,
      fieldIds: [...target.fieldIds],
    })),
  }));
  const targetFieldIds = Array.from(
    new Set(
      normalizedThenBlocks.flatMap((block) => block.targets.flatMap((target) => target.fieldIds))
    )
  );

  return {
    id: rule.id,
    name: rule.name,
    triggerFieldId: rule.triggerFieldId,
    triggerOption: rule.triggerOption,
    action: normalizedThenBlocks[0]?.action || rule.action,
    targetFieldIds,
    thenBlocks: normalizedThenBlocks,
  };
}

export function useSavedFormRules({
  activeDraftThenBlockId,
  confirmationMessages,
  getSelectionSignature,
  lastSelectionSignature,
  openConfirmationModal,
}: UseSavedFormRulesOptions) {
  const formBuilderStore = useFormBuilderStore();
  const formRulesStore = useFormRulesStore();
  const toast = useFormBuilderToast();
  const activeThenBlock = ref<{ ruleId: string; blockId: string } | null>(
    formRulesStore.savedThenItemSelection
  );
  const expandedRuleIds = ref<string[]>([...formRulesStore.expandedRuleIdsDraft]);
  const renderVersion = ref(0);

  const ruleIds = computed(() => formRulesStore.rules.map((rule) => rule.id));
  const hasAnyExpanded = computed(() =>
    ruleIds.value.some((ruleId) => expandedRuleIds.value.includes(ruleId))
  );
  const editingLocked = computed(() => formRulesStore.ruleItemSelectionActive);
  const flattenedFields = computed(() => flattenRuleFields(formBuilderStore.elements));
  const ruleTriggerOptionUsages = computed<RuleOptionUsage[]>(() => {
    const usages: RuleOptionUsage[] = [...formRulesStore.rules];
    const creationDraft = formRulesStore.ruleCreationDraft;

    if (creationDraft) {
      appendRuleOptionUsage(
        usages,
        ACTIVE_RULE_CREATION_DRAFT_USAGE_ID,
        creationDraft.triggerFieldId,
        creationDraft.triggerOption
      );
    }

    formRulesStore.pendingCreationDrafts.forEach((draft, index) => {
      appendRuleOptionUsage(
        usages,
        getPendingRuleCreationDraftUsageId(index),
        draft.triggerFieldId,
        draft.triggerOption
      );
    });

    return usages;
  });
  const selectedFieldRule = computed(() => {
    if (formRulesStore.scopeType !== 'single' || formRulesStore.selectedFieldIds.length !== 1) {
      return null;
    }

    const [selectedFieldId] = formRulesStore.selectedFieldIds;
    return formRulesStore.rules.find((rule) => rule.triggerFieldId === selectedFieldId) ?? null;
  });

  function getTriggerSelectOptions(rule: {
    id: string;
    triggerFieldId: string;
    triggerOption: string;
  }) {
    const field =
      flattenedFields.value.find((candidate) => candidate.id === rule.triggerFieldId) ?? null;
    const availableOptions = getAvailableRuleTriggerOptions(
      field,
      ruleTriggerOptionUsages.value,
      rule.id
    );

    return getRuleTriggerOptions(field)
      .filter(
        (option) =>
          option.value === rule.triggerOption ||
          availableOptions.some((availableOption) => availableOption.value === option.value)
      )
      .map((option) => ({
        id: option.value,
        name: option.label,
      }));
  }

  function getTargetOptionsForTrigger(triggerFieldId: string): ConditionalRuleTarget[] {
    return getRuleTargetOptions(formBuilderStore.elements, flattenedFields.value, triggerFieldId);
  }

  function getSelectedTargets(rule: RuleView) {
    const options = getTargetOptionsForTrigger(rule.triggerFieldId);

    if (formRulesStore.scopeType === 'all') {
      return options.filter((target) => target.type === 'field');
    }

    const menuAnchor = formRulesStore.menuAnchor;
    if (menuAnchor.kind === 'table-scope' && menuAnchor.scope === 'table') {
      const tableTarget = options.find(
        (target) => target.type === 'table' && target.id === menuAnchor.tableId
      );
      return tableTarget ? [tableTarget] : [];
    }

    return formRulesStore.selectedFields
      .filter((field) => field.id !== rule.triggerFieldId)
      .map((field) => options.find((target) => target.type === 'field' && target.id === field.id))
      .filter((target): target is ConditionalRuleTarget => !!target);
  }

  function patchRule(
    rule: RuleView,
    patch: Partial<Pick<ConditionalFormRule, 'name' | 'triggerOption' | 'action'>> & {
      thenBlocks?: readonly RuleThenBlockView[];
    }
  ) {
    const nextThenBlocks = patch.thenBlocks ?? rule.thenBlocks;
    try {
      formRulesStore.patchRule({
        ...buildRulePatch(rule, nextThenBlocks),
        name: patch.name ?? rule.name,
        triggerOption: patch.triggerOption ?? rule.triggerOption,
        action: patch.action ?? (nextThenBlocks[0]?.action || rule.action),
      });
      renderVersion.value += 1;
    } catch (error) {
      if (isBlankRuleNameError(error)) {
        toast.showErrorToast({ title: 'O nome da regra é obrigatório.', type: 'error' });
        renderVersion.value += 1;
        return;
      }

      if (isRuleCycleError(error)) {
        toast.showErrorToast({
          title: ruleCycleSaveMessage(
            error.cycles.map((ids) => elementLabelsById(formBuilderStore.formStructure, ids))
          ),
          type: 'error',
        });
        renderVersion.value += 1;
        formRulesStore.clearSelection();
        formRulesStore.setDraftTargetFieldIds([]);
        return;
      }

      throw error;
    }
  }

  function syncSelectedTargets() {
    const activeSelection = activeThenBlock.value;
    if (!activeSelection) return false;

    const activeRule = formRulesStore.rules.find((rule) => rule.id === activeSelection.ruleId);
    const activeBlock = activeRule?.thenBlocks.find(
      (block) => block.id === activeSelection.blockId
    );
    if (!activeRule || !activeBlock?.action) return true;

    const targets = getSelectedTargets(activeRule);
    if (targets.length === 0) return true;

    const nextTargets = [
      ...activeBlock.targets,
      ...targets.filter(
        (target) => !activeBlock.targets.some((existing) => existing.id === target.id)
      ),
    ];
    const nextThenBlocks = activeRule.thenBlocks.map((block) =>
      block.id === activeBlock.id ? { ...block, targets: nextTargets } : block
    );
    patchRule(activeRule, { thenBlocks: nextThenBlocks });
    return true;
  }

  function clearActiveSelection(ruleId: string) {
    if (activeThenBlock.value?.ruleId !== ruleId) return;

    activeThenBlock.value = null;
    formRulesStore.clearSelection();
    formRulesStore.setDraftTargetFieldIds([]);
  }

  function applyTriggerOptionUpdate(rule: RuleView, value: string | number) {
    const firstBlock = rule.thenBlocks[0];
    const nextThenBlocks: RuleThenBlockView[] = [
      {
        id: firstBlock?.id ?? `then-${Date.now()}`,
        action: '',
        targets: [],
      },
    ];

    patchRule(rule, { triggerOption: String(value), thenBlocks: nextThenBlocks });
    clearActiveSelection(rule.id);
  }

  function updateTriggerOption(rule: RuleView, value: string | number) {
    if (value === rule.triggerOption) return;

    const hasConfiguredThenBlock = rule.thenBlocks.some(
      (block) => block.action || block.targets.length > 0
    );
    if (!hasConfiguredThenBlock) {
      applyTriggerOptionUpdate(rule, value);
      return;
    }

    openConfirmationModal(
      confirmationMessages.changeTriggerOption.title,
      confirmationMessages.changeTriggerOption.description,
      () => applyTriggerOptionUpdate(rule, value)
    );
  }

  function updateRuleName(rule: RuleView, value: string) {
    formRulesStore.setCurrentRule(rule.id);
    if (!value.trim()) {
      toast.showErrorToast({ title: 'O nome da regra é obrigatório.', type: 'error' });
      renderVersion.value += 1;
      return;
    }

    if (value === rule.name) return;
    patchRule(rule, { name: value });
  }

  function startThenItemSelection(ruleId: string, blockId: string) {
    if (activeThenBlock.value || activeDraftThenBlockId.value) return;

    expandedRuleIds.value = [ruleId];
    activeThenBlock.value = { ruleId, blockId };
    formRulesStore.clearSelection();
    lastSelectionSignature.value = getSelectionSignature();
  }

  function updateThenAction(rule: RuleView, block: RuleThenBlockView, value: string | number) {
    if (!String(value)) return;

    const action = value as ConditionalRuleAction;
    const nextThenBlocks = rule.thenBlocks.map((thenBlock) =>
      thenBlock.id === block.id ? { ...thenBlock, action } : thenBlock
    );

    patchRule(rule, {
      action: rule.thenBlocks[0]?.id === block.id ? action : rule.action,
      thenBlocks: nextThenBlocks,
    });
  }

  function handleThenAction(rule: RuleView, payload: SavedThenActionPayload) {
    updateThenAction(rule, payload.block, payload.value);
  }

  function removeThenBlock(rule: RuleView, block: RuleThenBlockView, index: number) {
    if (index === 0) {
      const nextThenBlocks: RuleThenBlockView[] = rule.thenBlocks.map((thenBlock) =>
        thenBlock.id === block.id ? { ...thenBlock, action: '', targets: [] } : thenBlock
      );
      patchRule(rule, { thenBlocks: nextThenBlocks });
      toast.showSuccessToast({ title: 'Itens removidos com sucesso', type: 'success' });
      return;
    }

    patchRule(rule, {
      thenBlocks: rule.thenBlocks.filter((thenBlock) => thenBlock.id !== block.id),
    });
    toast.showSuccessToast({ title: 'Seção removida com sucesso', type: 'success' });
  }

  function handleThenDelete(rule: RuleView, payload: SavedThenIndexedPayload) {
    openConfirmationModal(
      confirmationMessages.clearThenItems.title,
      confirmationMessages.clearThenItems.description,
      () => removeThenBlock(rule, payload.block, payload.index)
    );
  }

  function addThenItems(rule: RuleView, block: RuleThenBlockView) {
    if (!block.action) return;
    startThenItemSelection(rule.id, block.id);
  }

  function confirmThenItems() {
    const activeSelection = activeThenBlock.value;
    if (!activeSelection) return;

    const activeRule = formRulesStore.rules.find((rule) => rule.id === activeSelection.ruleId);
    const activeBlock = activeRule?.thenBlocks.find(
      (block) => block.id === activeSelection.blockId
    );

    activeThenBlock.value = null;
    formRulesStore.clearSelection();
    formRulesStore.setDraftTargetFieldIds([]);
    if (activeBlock?.targets.length) {
      toast.showSuccessToast({ title: 'Itens adicionados com sucesso', type: 'success' });
    }
  }

  function removeTarget(rule: RuleView, block: RuleThenBlockView, targetId: string) {
    const nextTargets = block.targets.filter((target) => target.id !== targetId);
    const nextThenBlocks = rule.thenBlocks.map((thenBlock) =>
      thenBlock.id === block.id
        ? {
            ...thenBlock,
            targets: nextTargets,
          }
        : thenBlock
    );

    patchRule(rule, { thenBlocks: nextThenBlocks });

    if (
      nextTargets.length === 0 &&
      activeThenBlock.value?.ruleId === rule.id &&
      activeThenBlock.value?.blockId === block.id
    ) {
      activeThenBlock.value = null;
      formRulesStore.clearSelection();
      formRulesStore.setDraftTargetFieldIds([]);
    }

    toast.showSuccessToast({ title: 'Item removido com sucesso!', type: 'success' });
  }

  function handleTargetRemoval(
    rule: RuleView,
    payload: { block: RuleThenBlockView; targetId: string }
  ) {
    removeTarget(rule, payload.block, payload.targetId);
  }

  function addThenBlock(rule: RuleView) {
    if (rule.thenBlocks.length >= 3) return;
    const nextBlock: ConditionalThenBlock = {
      id:
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? `then-${crypto.randomUUID()}`
          : `then-${Date.now()}`,
      action: '',
      targets: [],
    };
    patchRule(rule, { thenBlocks: [...rule.thenBlocks, nextBlock] });
    expandedRuleIds.value = Array.from(new Set([...expandedRuleIds.value, rule.id]));
  }

  function isExpanded(ruleId: string) {
    return expandedRuleIds.value.includes(ruleId);
  }

  function removeRule(ruleId: string) {
    formRulesStore.removeRule(ruleId);
    expandedRuleIds.value = expandedRuleIds.value.filter(
      (expandedRuleId) => expandedRuleId !== ruleId
    );
    clearActiveSelection(ruleId);
    toast.showSuccessToast({ title: 'Regra removida com sucesso', type: 'success' });
  }

  function requestRuleRemoval(ruleId: string) {
    openConfirmationModal(
      confirmationMessages.deleteRule.title,
      confirmationMessages.deleteRule.description,
      () => removeRule(ruleId)
    );
  }

  function toggleRule(ruleId: string) {
    if (editingLocked.value) return;

    if (isExpanded(ruleId)) {
      expandedRuleIds.value = expandedRuleIds.value.filter((id) => id !== ruleId);
      clearActiveSelection(ruleId);
      return;
    }

    expandedRuleIds.value = [...expandedRuleIds.value, ruleId];
  }

  function toggleAll() {
    if (editingLocked.value) return;

    const shouldCollapse = hasAnyExpanded.value;
    expandedRuleIds.value = shouldCollapse ? [] : [...ruleIds.value];

    if (shouldCollapse) {
      formRulesStore.cancelRuleCreation();
    }
  }

  watch(ruleIds, (ids) => {
    expandedRuleIds.value = expandedRuleIds.value.filter((ruleId) => ids.includes(ruleId));
    if (activeThenBlock.value && !ids.includes(activeThenBlock.value.ruleId)) {
      activeThenBlock.value = null;
    }
  });

  watch(
    activeThenBlock,
    (selection) => {
      formRulesStore.setSavedThenItemSelection(selection);
    },
    { deep: true }
  );

  watch(
    expandedRuleIds,
    (ids) => {
      formRulesStore.setExpandedRuleIdsDraft(ids);
    },
    { deep: true }
  );

  watch(
    () => formRulesStore.savedThenItemSelection,
    (selection) => {
      if (
        activeThenBlock.value?.ruleId === selection?.ruleId &&
        activeThenBlock.value?.blockId === selection?.blockId
      ) {
        return;
      }

      activeThenBlock.value = selection ? { ...selection } : null;
    }
  );

  watch(
    () => formRulesStore.expandedRuleIdsDraft,
    (ids) => {
      if (JSON.stringify(expandedRuleIds.value) === JSON.stringify(ids)) {
        return;
      }

      expandedRuleIds.value = [...ids];
    }
  );

  watch(selectedFieldRule, (rule) => {
    if (!rule || formRulesStore.ruleItemSelectionActive) return;
    expandedRuleIds.value = Array.from(new Set([...expandedRuleIds.value, rule.id]));
  });

  watch(
    () => [formRulesStore.focusedRuleId, formRulesStore.focusedRuleVersion] as const,
    ([ruleId]) => {
      if (!ruleId || formRulesStore.ruleItemSelectionActive) return;
      expandedRuleIds.value = Array.from(new Set([...expandedRuleIds.value, ruleId]));
    }
  );

  return {
    activeThenBlock,
    editingLocked,
    expandedRuleIds,
    hasAnyExpanded,
    renderVersion,
    ruleIds,
    addThenBlock,
    addThenItems,
    confirmThenItems,
    getTriggerSelectOptions,
    handleTargetRemoval,
    handleThenAction,
    handleThenDelete,
    isExpanded,
    removeRule: requestRuleRemoval,
    syncSelectedTargets,
    toggleAll,
    toggleRule,
    updateRuleName,
    updateTriggerOption,
  };
}
